import React, { useEffect, useRef, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';
import * as Yup from 'yup';
import qs from 'querystring';
import { isArray, assign } from 'lodash';
import { format } from 'date-fns';

import { Autocomplete, RadioGroup, Input } from '~/components/Form';
import { Select } from '~/components/Form/v2';
import { t } from '~/helpers';
import api from '~/services/api';

import * as S from './styles';

export default function MyForm({
  data,
  close,
  sending,
  onForm,
  toggleDisabled,
}) {
  const formRef = useRef(null);

  const [vehicles, setVehicles] = useState([]);

  const { username } = useSelector((state) => state.user.profile);

  const handleSubmit = useCallback(
    async (value) => {
      try {
        await toggleDisabled(true);

        const regex = /^[a-z A-Z 0-9 ãõáéíóúñÁÉÍÓÚÑ \(\)\[\]\/\:\?\!\@\$\%\,\-\.\+";]+$/;

        const schema = Yup.object().shape({
          rNewEmpe_Cod_Emp: Yup.string().required('Campo obrigatório.'),
          rNewVeic_Cod_Veic: Yup.string().required('Campo obrigatório.'),
          rNewMoto_cod_moto: Yup.string().required('Campo obrigatório.'),
          rMaag_Usua_Sol: Yup.string().required('Campo obrigatório.'),
          rNewEnvia_Unilog: Yup.string().required('Campo obrigatório.'),
          rMaag_Obs: Yup.string()
            .required('Campo obrigatório.')
            .max(500, 'São permitidos apenas 500 caracteres.')
            .matches(regex, 'Caracteres inválidos!'),
        });

        await schema.validate(value, { abortEarly: false });

        const formData = assign(
          {
            rCodigo: data.abas_cod_abas,
            rMaag_Tabela: 'ABASTECIMENTO',
            rMata_Cod_Mata: 3,
            rMaag_Num_Tar_Exec: 3,
            rMaag_Chave: `(POST_COD_POSTO = &#183${data.post_cod_posto}&#183 AND ABAS_COD_ABAS = ${data.abas_cod_abas})`,
            rPost_Cod_Posto: data.post_cod_posto,
            rBico_cod_bico: data.bico_cod_bico,
            rTurn_cod_turn: data.turn_cod_turn,
            rMaag_Usua_Inc: username,
            rAtualAbas_Vol_Amb: data.abas_vol_amb,
            rAtualAbas_Vol_Ref: data.abas_vol_ref,
            rAtualAbas_Vol_20: data.abas_vol_20,
            rNF_AlterarVolumes: 'N',
            rNewNumPedido: '',
            rNewNumNfRvt: '',
            rNewNumCTVT: '',
            rNewDataEmissaoNF: format(new Date(), 'dd/MM/yyyy'),
            rAtualVolNF: '0.0',
            rAtualVol20NF: '0.0',
            rAtualTemperaturaNF: '0.0',
            rAtualDensidadeNF: '0.0',
            rOldVolNF: '0.0',
            rOldVol20NF: '0.0',
          },
          value
        );

        await api.post('manutencaocontrole', formData);

        toast.success('Manutenção agendada com sucesso!');

        await onForm();
        await close();
      } catch (err) {
        const validationErrors = {};

        if (err instanceof Yup.ValidationError) {
          err.inner.forEach((error) => {
            validationErrors[error.path] = error.message.replace(
              `${error.path} `,
              ''
            );
          });

          formRef.current.setErrors(validationErrors);
        } else {
          if (isArray(err)) err.map((e) => toast.error(t(e)));
          else toast.error(t(err));
        }
      } finally {
        await toggleDisabled(false);
      }
    },
    [data, username]
  );

  const handleChangeCompany = useCallback(async (value) => {
    try {
      setVehicles([]);

      formRef.current.setFieldValue('rNewVeic_Cod_Veic', '');

      if (value.empe_cod_emp) {
        const response = await api.get('veiculo', {
          params: {
            wherefield: ['empe_cod_emp'],
            valuewherefield: [value.empe_cod_emp],
            typewherefield: ['EQUALS'],
            pagesize: 9999,
            pesquisalista: true,
          },
          paramsSerializer: (p) => qs.stringify(p),
        });

        setVehicles(
          response.data.items.map((i) => ({
            label: i.veic_cod_veic,
            value: i.veic_cod_veic,
          }))
        );
      }
    } catch (err) {
      if (isArray(err)) err.map((e) => toast.error(t(e)));
      else toast.error(t(err));
    }
  }, []);

  useEffect(() => {
    if (sending) formRef.current.submitForm();
  }, [sending]);

  return (
    <S.WrapperForm
      ref={formRef}
      onSubmit={handleSubmit}
      initialData={{ rNewEnvia_Unilog: 'N' }}
    >
      <Autocomplete
        name="rNewEmpe_Cod_Emp"
        label="empe_desc"
        unique="empe_cod_emp"
        url="veiculo/empresas"
        title="Empresa"
        getValue={handleChangeCompany}
      />

      <Select name="rNewVeic_Cod_Veic" label="Veículo" data={vehicles} />

      <Autocomplete
        name="rNewMoto_cod_moto"
        label="moto_nome"
        unique="moto_cod_moto"
        url="maquinista"
        title="Maquinista"
      />

      <Autocomplete
        name="rMaag_Usua_Sol"
        label="usua_nome"
        unique="usua_cod_usua"
        url="usuario"
        title="Usuário Solicitante"
      />

      <RadioGroup
        row
        name="rNewEnvia_Unilog"
        label="Retransmitir para cliente"
        options={[
          { label: 'Sim', value: 'S' },
          { label: 'Não', value: 'N' },
        ]}
      />

      <Input multiline name="rMaag_Obs" label="MSG" rows={4} />
    </S.WrapperForm>
  );
}

MyForm.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  close: PropTypes.func.isRequired,
  sending: PropTypes.bool.isRequired,
  onForm: PropTypes.func.isRequired,
  toggleDisabled: PropTypes.func.isRequired,
};
